import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { tournamentsApi, statsApi } from '../lib/api';
import MaraSportsLogo from '../components/MaraSportsLogo';
import {
    Trophy, Calendar, MapPin, Users, Search, Filter,
    ChevronRight, ArrowLeft, Plus, DollarSign
} from 'lucide-react';

const TournamentsPage = () => {
    const navigate = useNavigate();
    const [tournaments, setTournaments] = useState([]);
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        sport: '',
        status: '',
        search: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [tournamentsRes, sportsRes] = await Promise.all([
                tournamentsApi.getAll(),
                statsApi.getSports()
            ]);
            setTournaments(tournamentsRes.data);
            setSports(sportsRes.data.sports);
        } catch (error) {
            console.error('Failed to load tournaments:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.sport && filters.sport !== 'all') params.sport = filters.sport;
            if (filters.status && filters.status !== 'all') params.tournament_status = filters.status;
            const response = await tournamentsApi.getAll(params);
            setTournaments(response.data);
        } catch (error) {
            console.error('Failed to filter tournaments:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (tournament_status) => {
        switch (tournament_status) {
            case 'registration_open': return 'text-success bg-success/10 border-success/20';
            case 'in_progress': return 'text-secondary bg-secondary/10 border-secondary/20';
            case 'upcoming': return 'text-accent bg-accent/10 border-accent/20';
            case 'completed': return 'text-muted-foreground bg-muted border-white/10';
            default: return 'text-muted-foreground bg-muted border-white/10';
        }
    };

    const getStatusLabel = (tournament_status) => {
        switch (tournament_status) {
            case 'registration_open': return 'Registration Open';
            case 'in_progress': return 'In Progress';
            case 'upcoming': return 'Upcoming';
            case 'completed': return 'Completed';
            default: return tournament_status;
        }
    };

    const filteredTournaments = tournaments.filter(t => {
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            return t.name.toLowerCase().includes(searchLower) || 
                   t.location.toLowerCase().includes(searchLower);
        }
        return true;
    });

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-4 h-4" />
                            </Link>
                            <Link to="/">
                                <MaraSportsLogo className="h-10 hidden sm:flex" />
                            </Link>
                        </div>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            variant="outline"
                            className="border-white/20 text-sm"
                            data-testid="nav-dashboard"
                        >
                            Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative py-16 md:py-24 border-b border-white/5">
                <div className="hero-glow absolute inset-0 z-0" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
                    <p className="text-xs tracking-widest uppercase text-primary mb-4">Competition Infrastructure</p>
                    <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter uppercase mb-4">
                        Tournaments
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-xl">
                        Browse upcoming competitions, register for events, and track your tournament journey across Africa.
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section className="py-6 border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search tournaments..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="pl-10 bg-[#0d2847] border-white/10 h-11"
                                data-testid="tournament-search"
                            />
                        </div>
                        <Select
                            value={filters.sport}
                            onValueChange={(value) => setFilters({ ...filters, sport: value })}
                        >
                            <SelectTrigger className="w-full md:w-48 bg-[#0d2847] border-white/10 h-11" data-testid="filter-sport">
                                <SelectValue placeholder="All Sports" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="all">All Sports</SelectItem>
                                {sports.map((sport) => (
                                    <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={filters.status}
                            onValueChange={(value) => setFilters({ ...filters, status: value })}
                        >
                            <SelectTrigger className="w-full md:w-48 bg-[#0d2847] border-white/10 h-11" data-testid="filter-status">
                                <SelectValue placeholder="All Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="registration_open">Registration Open</SelectItem>
                                <SelectItem value="upcoming">Upcoming</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={applyFilters}
                            className="bg-primary hover:bg-red-600 text-white h-11"
                            data-testid="apply-filters"
                        >
                            <Filter className="w-4 h-4 mr-2" />
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </section>

            {/* Tournaments List */}
            <section className="py-12">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    {loading ? (
                        <div className="flex items-center justify-center py-24">
                            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : filteredTournaments.length === 0 ? (
                        <div className="text-center py-24">
                            <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-heading font-bold text-xl uppercase mb-2">No Tournaments Found</h3>
                            <p className="text-muted-foreground">Try adjusting your filters or check back later.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredTournaments.map((tournament) => (
                                <Link
                                    key={tournament.id}
                                    to={`/tournaments/${tournament.id}`}
                                    className="bg-[#0d2847] border border-white/5 hover:border-white/10 transition-all card-hover group"
                                    data-testid={`tournament-${tournament.id}`}
                                >
                                    {/* Card Header */}
                                    <div className="p-6 border-b border-white/5">
                                        <div className="flex items-start justify-between gap-4 mb-4">
                                            <span className={`text-xs px-3 py-1 border ${getStatusColor(tournament.tournament_status)}`}>
                                                {getStatusLabel(tournament.tournament_status)}
                                            </span>
                                            <span className="text-xs text-muted-foreground">{tournament.sport}</span>
                                        </div>
                                        <h3 className="font-heading font-bold text-xl uppercase tracking-tight mb-2 group-hover:text-primary transition-colors">
                                            {tournament.name}
                                        </h3>
                                        <p className="text-sm text-muted-foreground line-clamp-2">
                                            {tournament.description || 'Join this exciting competition and showcase your skills.'}
                                        </p>
                                    </div>

                                    {/* Card Details */}
                                    <div className="p-6 space-y-3">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <MapPin className="w-4 h-4" />
                                            <span>{tournament.location}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Calendar className="w-4 h-4" />
                                            <span>{tournament.start_date} - {tournament.end_date}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Users className="w-4 h-4" />
                                            <span>{tournament.current_participants} / {tournament.max_participants} participants</span>
                                        </div>
                                        {tournament.prize_pool > 0 && (
                                            <div className="flex items-center gap-2 text-sm text-accent">
                                                <DollarSign className="w-4 h-4" />
                                                <span>
                                                    {tournament.currency === 'KES' ? 'KSh ' : tournament.currency === 'USD' ? '$' : (tournament.currency || '$')}
                                                    {tournament.prize_pool.toLocaleString()} Prize Pool
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Card Footer */}
                                    <div className="p-6 border-t border-white/5 flex items-center justify-between">
                                        <div className="flex flex-wrap gap-2">
                                            {tournament.age_categories?.slice(0, 3).map((cat) => (
                                                <span key={cat} className="text-xs px-2 py-1 bg-white/5 text-muted-foreground">
                                                    {cat}
                                                </span>
                                            ))}
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default TournamentsPage;
